import type { JSONContent } from '@tiptap/react';

import { ApiError } from '@/domains/api';
import { formatDateTime } from '@/domains/survey';
import type {
  ILinePart,
  IMailAccount,
  IMailBody,
  IMailPartDefinition,
  IMailProblem,
  IMailSettings,
  IMailTemplate,
  IRichPart,
  MailPart,
  MailParts,
  MailSurvey
} from '../../api/mail-template-contracts';
import { docKey, emptyRich } from './rich-part';

/**
 * A template's parts as the editor holds them — the template declares them (`parts`), a version
 * or a draft fills them — plus the sender settings, the problems by part and the quality hints.
 */

export const SURVEYS: readonly { value: MailSurvey; label: string }[] = [
  { value: 'FA', label: 'FA' },
  { value: 'FSP', label: 'FSP' },
  { value: 'RIA_FIRM', label: 'RIA' },
  { value: 'TEAMS', label: 'Teams' }
];

export function surveyLabel(survey: Nullable<MailSurvey>): string {
  return survey ? (SURVEYS.find((entry) => entry.value === survey)?.label ?? survey) : 'All surveys';
}

export function isRich(part: MailPart | undefined): part is IRichPart {
  return Boolean(part && 'doc' in part);
}

export function lineText(part: MailPart | undefined): string {
  return part && 'text' in part ? (part as ILinePart).text : '';
}

export const SETTING_KEYS = ['fromName', 'replyTo', 'design'] as const;

export const DEFAULT_SETTINGS: IMailSettings = { fromName: '', replyTo: '', design: 'veritant' };

/** A body with every declared part present — a part the stored one lacks starts empty. */
export function bodyOf(definitions: readonly IMailPartDefinition[], body: Nullable<Partial<IMailBody>>): IMailBody {
  const parts: MailParts = {};
  for (const definition of definitions) {
    const part = body?.parts?.[definition.key];
    parts[definition.key] =
      definition.kind === 'rich' ? (isRich(part) ? part : emptyRich()) : { text: lineText(part) };
  }
  const settings = { ...DEFAULT_SETTINGS, ...(body?.settings ?? {}) };
  return { parts, settings: { ...settings, design: settings.design || DEFAULT_SETTINGS.design } };
}

function samePart(a: MailPart | undefined, b: MailPart | undefined): boolean {
  if (isRich(a) || isRich(b)) return isRich(a) && isRich(b) && docKey(a.doc) === docKey(b.doc);
  return lineText(a) === lineText(b);
}

export function sameBody(definitions: readonly IMailPartDefinition[], a: IMailBody, b: IMailBody): boolean {
  return (
    definitions.every(({ key }) => samePart(a.parts[key], b.parts[key])) &&
    SETTING_KEYS.every((key) => (a.settings[key] ?? '') === (b.settings[key] ?? ''))
  );
}

/** The parts that differ between two bodies, and the settings. */
export function changedKeys(definitions: readonly IMailPartDefinition[], a: IMailBody, b: IMailBody): string[] {
  return [
    ...definitions.filter(({ key }) => !samePart(a.parts[key], b.parts[key])).map(({ key }) => key),
    ...SETTING_KEYS.filter((key) => (a.settings[key] ?? '') !== (b.settings[key] ?? ''))
  ];
}

/** "Version N · date · email" (`short`: "vN · …"), or "Default" for version 0. */
export function versionStamp(
  version: number,
  at: Nullable<string>,
  by: Nullable<IMailAccount>,
  short = false
): string {
  if (version === 0) return 'Default';
  return [short ? `v${version}` : `Version ${version}`, at ? formatDateTime(at) : null, by?.email ?? null]
    .filter(Boolean)
    .join(' · ');
}

/** An instant as the business zone reads it: "Nov 15, 2026, 9:00 AM ET". */
export function formatEt(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return `${date.toLocaleString('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })} ET`;
}

/** "just now", "5 min ago", "3 h ago", or the date. */
export function formatAgo(iso: string, now = Date.now()): string {
  const at = new Date(iso).getTime();
  if (Number.isNaN(at)) return iso;
  const minutes = Math.floor((now - at) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 24 * 60) return `${Math.floor(minutes / 60)} h ago`;
  return formatDateTime(iso);
}

/** Problems by part key, or by setting (`fromName`, `replyTo`). */
export type FieldProblems = Partial<Record<string, string>>;

/** Past this many characters of the filled subject, an inbox often cuts it. */
const SUBJECT_ROOM = 60;

/** Mostly capitals: at least 8 letters, 70% of them upper case. Placeholders do not count. */
function shouts(value: string): boolean {
  const letters = value.replace(/\{\{[^{}]*\}\}/g, '').match(/\p{L}/gu) ?? [];
  const upper = letters.filter((letter) => letter !== letter.toLowerCase());
  return letters.length >= 8 && upper.length >= 0.7 * letters.length;
}

/** The text of a body's headings. */
function headingsOf(doc: JSONContent): string[] {
  const text = (node: JSONContent): string =>
    node.type === 'text' ? (node.text ?? '') : (node.content ?? []).map(text).join('');
  return (doc.content ?? []).filter((node) => node.type === 'heading').map(text);
}

/** What the mail may do badly in an inbox, by part — said muted, never blocking a save. `filled`
 *  is the subject the preview rendered; `fallbackContact` undefined when survey-api does not say. */
export function qualityHints(
  template: IMailTemplate,
  mail: IMailBody,
  filled: Nullable<string>
): FieldProblems {
  const has = (key: string) => template.parts.some((part) => part.key === key && part.kind === 'line');
  const text = (key: string) => lineText(mail.parts[key]);
  const hints: FieldProblems = {};
  if (has('subject')) {
    const subject: string[] = [];
    if (filled !== null && filled.length > SUBJECT_ROOM) {
      subject.push(`Inboxes often cut subjects after about ${SUBJECT_ROOM} characters (now ${filled.length}).`);
    }
    if (shouts(text('subject'))) subject.push('Mostly capitals: it reads as shouting.');
    if (subject.length > 0) hints.subject = subject.join(' ');
  }
  if (has('preheader') && !text('preheader').trim()) {
    hints.preheader = 'No preview text: the inbox shows the first line of the mail.';
  }
  const body = mail.parts.body;
  if (isRich(body) && headingsOf(body.doc).some(shouts)) {
    hints.body = 'A heading in mostly capitals reads as shouting.';
  }
  return hints;
}

/** Why survey-api would refuse a setting, or null — its own rules, said before it is asked. */
export function settingProblems(settings: IMailSettings): FieldProblems {
  const problems: FieldProblems = {};
  if (settings.fromName.length > 80) problems.fromName = 'At most 80 characters.';
  else if (/[<>"]/.test(settings.fromName)) problems.fromName = 'No <, > or " in a name.';
  const replyTo = settings.replyTo.trim();
  if (replyTo && !/^[^\s@<>",]+@[^\s@<>",]+\.[^\s@<>",]+$/.test(replyTo)) {
    problems.replyTo = 'One plain address, such as research@veritant.com.';
  }
  return problems;
}

export interface ISortedProblems {
  byField: FieldProblems;
  /** Whatever names no part, as one line. */
  other: Nullable<string>;
}

/** A refused body's problems by part — survey-api's `problems[]`, or ASP.NET's `errors`. */
export function problemsOf(error: unknown, keys: readonly string[]): ISortedProblems {
  if (!(error instanceof ApiError)) return { byField: {}, other: null };
  return sortProblems(
    [
      ...(error.problem?.problems ?? []),
      ...Object.entries(error.problem?.errors ?? {}).map(([target, reasons]) => ({
        target,
        reason: reasons.join(' ')
      }))
    ],
    keys
  );
}

/** `parts.text`, `settings.replyTo` or a bare `subject`, to the key it names among `keys`. */
export function sortProblems(entries: readonly IMailProblem[], keys: readonly string[]): ISortedProblems {
  const byField: FieldProblems = {};
  const other: string[] = [];
  const all = [...keys, ...SETTING_KEYS];
  for (const { target, reason } of entries) {
    const segments = target.split('.');
    const key = all.find((entry) =>
      segments.some((segment) => segment.toLowerCase() === entry.toLowerCase())
    );
    if (key) byField[key] = byField[key] ? `${byField[key]} ${reason}` : reason;
    else other.push(`${target}: ${reason}`);
  }
  return { byField, other: other.length > 0 ? other.join('; ') : null };
}

export function isInvalidBody(error: unknown): boolean {
  return error instanceof ApiError && error.status === 400;
}
